import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-apollo';
import getErrorDetail from '@jetshop/core/helpers/getErrorDetail';
import { ProductListContext } from '@jetshop/core/hooks/ProductList/ProductListContext';
import { flattenList } from '@jetshop/core/hooks/ProductList/list-transforms';
import { validateProduct } from '@jetshop/core/hooks/ProductList/validate-product';

export default function useSafeFavoriteProducts() {
  const context = useContext(ProductListContext);
  const listId = null;
  const list = context?.state?.lists?.get(listId);
  const flattenedList = useMemo(() => flattenList(list), [list]);
  const [hydrated, setHydrated] = useState(!context?.usingLocalList);

  const queryResult = useQuery(context.queries.productsQuery, {
    skip: flattenedList.length === 0,
    errorPolicy: 'all',
    variables: {
      articleNumbers: flattenedList.map(item => item.articleNumber)
    }
  });

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!queryResult.error) return;

    const { codes, data } = getErrorDetail(queryResult.error);
    if (codes.includes('ProductNotFound')) {
      data?.forEach(item => {
        context.dispatch({
          type: 'REMOVE',
          payload: { listId, articleNumber: item.argument }
        });
      });
    }
  }, [context, queryResult.error]);

  const products = useMemo(() => {
    const fetchedProducts = queryResult.data?.products || [];

    return flattenedList.reduce((result, listItem) => {
      const baseArticleNumber =
        listItem.parentArticleNumber || listItem.articleNumber;
      const product = fetchedProducts.find(
        item => item?.articleNumber === baseArticleNumber
      );
      if (!product?.stockStatus) return result;

      const variant = listItem.parentArticleNumber
        ? product.variants?.values?.find(
            item => item?.articleNumber === listItem.articleNumber
          )
        : null;
      // Skip deleted variants and incomplete stock data before validation.
      if (listItem.parentArticleNumber && !variant?.stockStatus) return result;

      const productListItem = {
        ...product,
        quantity: listItem.options?.quantity,
        isVariant: !!listItem.parentArticleNumber,
        variant
      };
      result.push({
        ...productListItem,
        validation: validateProduct(productListItem)
      });
      return result;
    }, []);
  }, [flattenedList, queryResult.data]);

  return {
    products,
    loading: hydrated ? queryResult.loading : true
  };
}
